import type { InvestmentExposureEntry } from "@eu.mdotm.api/sphere";
import { Card } from "$root/components/EvolvedPrint/components/Card";
import type { PrintableProps } from "$root/components/EvolvedPrint/configuration/types";
import {
	firstPageHeaderHeight,
	otherPagesHeaderHeight,
	pageFooterHeight,
} from "$root/components/EvolvedPrint/configuration/shared";
import { useLocaleFormatters } from "$root/localization/hooks";
import { stableColorGenerator, sumArrayLike } from "$root/utils";
import { type ExposureChartData, ExposureChart } from "$root/widgets-architecture/widgets/ExposureEvolve";
import { Text } from "@mdotm/shadcn/legacy/components";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { objectMap } from "@mdotm/shadcn/legacy/utils";
import { groupBy } from "@mdotm/utils/collections";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useReportTranslation } from "../translation";

export type ExposureListItem =
	| {
			type: "title";
			label: string;
			weight: number;
			groupName: string;
	  }
	| {
			type: "entry";
			label: string;
			weight: number;
			groupName: string;
			netLong: boolean;
	  }
	| {
			type: "footer";
			label: string;
			groupName: string;
	  };

export function exposureEntriesToSplittableProps(composition: InvestmentExposureEntry[]): Array<ExposureListItem> {
	const groups: string[] = [];
	for (const { firstQualityLevel } of composition) {
		if (!groups.includes(firstQualityLevel!)) {
			groups.push(firstQualityLevel!);
		}
	}

	return composition.length === 0
		? []
		: Array.from(
				Object.values(
					objectMap(
						groupBy(composition, (entry) => entry.firstQualityLevel!),
						(entries) => [
							{
								type: "title",
								label: entries?.at(0)?.firstQualityLevel ?? "",
								groupName: entries?.at(0)?.firstQualityLevel ?? "",
								weight: sumArrayLike(entries ?? [], (x) => x.weight!),
							} as const,
							...(entries ?? []).map(
								(x) =>
									({
										type: "entry",
										label: x.secondQualityLevel ?? "",
										weight: x.weight!,
										netLong: x.netLong ?? true,
										groupName: entries?.at(0)?.firstQualityLevel ?? "",
									}) as const,
							),
							{
								type: "footer",
								label: "",
								groupName: entries?.at(0)?.firstQualityLevel ?? "",
							} as const,
						],
					),
				),
			).flat();
}

export function Exposure(
	props: PrintableProps<
		{
			comparison: string | ((t: TFunction) => string);
			composition: Array<InvestmentExposureEntry>;
		},
		Array<ExposureListItem>
	>,
): React.JSX.Element {
	const { t } = useReportTranslation();

	const chartData = useMemo(() => {
		const data = props.composition.reduce(
			(acc, cur) => {
				if (!acc[cur.firstQualityLevel!]) {
					acc[cur.firstQualityLevel!] = {
						label: cur.firstQualityLevel!,
						weight: 0,
						drillDown: [],
						groupName: cur.firstQualityLevel!,
						netLong: cur.netLong ?? false,
					};
				}
				acc[cur.firstQualityLevel!].weight += cur.weight!;

				acc[cur.firstQualityLevel!].drillDown.push({
					label: cur.secondQualityLevel!,
					weight: cur.weight!,
					netLong: cur.netLong ?? true,
				});
				return acc;
			},
			{} as Record<string, ExposureChartData[number]>,
		);

		return Object.values(data);
	}, [props.composition]);

	if (props.list.length === 0) {
		return <></>;
	}

	// ${t(`REPORT_BUILDER.EXPOSURE.OPTIONS.${props.comparison}`)}
	return (
		<>
			{props.firstRender && (
				<Card
					style={{ marginBottom: 0 }}
					title={`${t("REPORT_BUILDER.EXPOSURE.TITLE")} - ${
						typeof props.comparison === "function" ? props.comparison(t) : props.comparison
					}`}
				>
					<div className="border-t border-b-2 pt-5 mb-4" style={{ borderColor: themeCSSVars.palette_N300 }}>
						<ExposureChart animated={false} customHeight={`${exposureChartHeight}px`} data={chartData} />
					</div>
				</Card>
			)}
			<ForEach collection={props.list}>
				{(subList) => (
					<CompositionList
						availableHeight={exposureAvailableHeightForItemSlotsByChartHeight(
							props.firstRender ? exposureChartSectionHeight(exposureChartHeight) : 0,
							"portrait",
						)}
						list={subList}
					/>
				)}
			</ForEach>
		</>
	);
}

export function CompositionList({
	availableHeight,
	list,
}: {
	availableHeight: number;
	list: Array<ExposureListItem>;
}): React.JSX.Element {
	const { formatNumber } = useLocaleFormatters();

	return (
		<div
			className="columns-3 px-12"
			style={{
				columnFill: "auto",
				height: availableHeight, // Math.ceil(list.length / exposureCompositionListItemsPerRow) * exposureCompositionListRowHeight,
			}}
		>
			{list.map((row, i) => (
				<div
					className="flex"
					style={{
						marginBottom: row.type === "footer" ? exposureCompositionListFooterRowMarginBottom : undefined,
					}}
					key={`exposure-singleColumn-item-key-${i}`}
				>
					<div className="pr-2 rounded-lg shrink-0">
						<div
							className={toClassName({
								"w-1 h-full": true,
								"rounded-t-full": row.type === "title",
								"rounded-b-full": row.type === "footer",
							})}
							style={{ backgroundColor: stableColorGenerator(row.groupName) }}
						/>
					</div>

					<div
						className={toClassName({
							"flex items-center justify-between flex-1 gap-2 overflow-hidden": true,
							"border-b": row.type !== "footer",
							uppercase: row.type === "title" || row.type === "footer",
						})}
						style={{ height: exposureCompositionListRowHeight, borderColor: themeCSSVars.palette_N200 }}
					>
						<Text
							as="div"
							className="line-clamp-2"
							color={row.type === "title" ? themeCSSVars.palette_N500 : undefined}
							type={row.type === "title" ? "Body/S/Bold" : "Body/S/Book"}
						>
							{row.label}
						</Text>

						<div>
							{row.type === "entry" && row.netLong === false && (
								<>
									&nbsp;-&nbsp;[&nbsp;
									<svg
										className="inline w-[8px] mb-1"
										width="8"
										height="8"
										viewBox="0 0 8 8"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<rect opacity="0.3" width="8" height="8" rx="4" fill={stableColorGenerator(row.groupName)} />
									</svg>
									&nbsp;
									<span className="truncate">Net Short</span>&nbsp;]
								</>
							)}
							{row.type !== "footer" && (
								<Text type={row.type === "title" ? "Body/S/Bold" : "Body/S/Book"}>{formatNumber(row.weight)}%</Text>
							)}
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

export const exposureCompositionListRowHeight = 40;
export const exposureCompositionListFooterRowMarginBottom = 16;
export const exposureCompositionListItemsPerRow = 3;
export const exposureChartHeight = 242;
export const exposureChartDecorationHeight = 119;
export const exposureChartSectionHeight = (baseChartHeight: number): number =>
	baseChartHeight + exposureChartDecorationHeight;
const exposurePortraitPageHeight = 1122 - Math.max(firstPageHeaderHeight, otherPagesHeaderHeight) - pageFooterHeight;
const exposureLandscapePageHeight = 320;

export function chunkExposureListItems(
	cells: Array<ExposureListItem>,
	baseChartHeight: number,
): Array<Array<ExposureListItem>> {
	if (cells.length === 0) {
		return [];
	}

	const chunks: Array<typeof cells> = [[]];

	let i = 0;
	let c = 0;
	let remainingHeight = exposureAvailableHeightForItemSlotsByChartHeight(
		exposureChartSectionHeight(baseChartHeight),
		"portrait",
	);
	let isFirstPage = true;
	while (i < cells.length) {
		const cell = cells[i];
		remainingHeight -= exposureCompositionListRowHeight;
		if (cell.type === "footer") {
			remainingHeight -= exposureCompositionListFooterRowMarginBottom;
		}
		if (remainingHeight < 0) {
			remainingHeight = exposureAvailableHeightForItemSlotsByChartHeight(
				isFirstPage ? exposureChartSectionHeight(baseChartHeight) : 0,
				"portrait",
			);
			c++;
			if (c === exposureCompositionListItemsPerRow) {
				isFirstPage = false;
				chunks.push([]);
				c = 0;
			}
		} else {
			chunks[chunks.length - 1].push(cell);
			i++;
		}
	}
	return chunks;
}

export function exposureAvailableHeightForItemSlotsByChartHeight(
	chartSectionHeight: number,
	layout: "portrait" | "landscape",
): number {
	return Math.max(
		0,
		(layout === "portrait" ? exposurePortraitPageHeight : exposureLandscapePageHeight) - chartSectionHeight,
	);
}
